const BOLT_PATH =
  "M13.6026 2.04035C13.8145 2.13104 13.937 2.35486 13.8991 2.58224L12.6785 9.90592H18.6765C18.8659 9.90592 19.039 10.0129 19.1237 10.1823C19.2084 10.3517 19.1901 10.5544 19.0765 10.7059L11.1706 21.2471C11.0323 21.4315 10.7858 21.4975 10.5739 21.4068C10.362 21.3161 10.2395 21.0923 10.2774 20.8649L11.498 13.5412H5.5C5.31062 13.5412 5.13749 13.4342 5.05279 13.2648C4.96809 13.0954 4.98637 12.8927 5.1 12.7412L13.0059 2.20004C13.1442 2.01563 13.3907 1.94967 13.6026 2.04035Z"

const Bolt = ({ filled }: { filled: boolean }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d={BOLT_PATH} fill={filled ? "#FF3366" : "#e5e7eb"} />
  </svg>
)

/**
 * Two compact pills used in candidate lists, next to the rating bolt.
 * Renders nothing when the candidate has no scores yet (a new candidate
 * should not look penalised with 0%).
 */
export const CandidateStatChips = ({
  responseRate,
  attendance,
}: {
  responseRate?: number | null
  attendance?: number | null
}) => {
  if (responseRate == null && attendance == null) return null
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {responseRate != null && (
        <span className="rounded-md bg-figmaLightGray px-2 py-[3px] text-[11px] font-semibold text-figmaDarkGray">
          <span className="font-bold text-figmaBlack">{Math.round(responseRate)}%</span> răsp
        </span>
      )}
      {attendance != null && (
        <span className="rounded-md bg-figmaLightGray px-2 py-[3px] text-[11px] font-semibold text-figmaDarkGray">
          <span className="font-bold text-figmaBlack">{Math.round(attendance)}%</span> prez
        </span>
      )}
    </div>
  )
}

/* ---- Compact stat strip for list cards ---------------------------------- */

const MiniRing = ({ percent }: { percent?: number | null }) => {
  const value = percent != null ? Math.max(0, Math.min(100, percent)) : 0
  return (
    <div
      className="flex h-[46px] w-[46px] items-center justify-center rounded-full"
      style={{ background: `conic-gradient(#486af6 ${value}%, #e7ebfd 0)` }}
    >
      <div className="flex h-[36px] w-[36px] items-center justify-center rounded-full bg-white">
        <span className="text-[11px] font-bold text-figmaBlack">
          {percent != null ? `${Math.round(percent)}%` : "—"}
        </span>
      </div>
    </div>
  )
}

const StripCell = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="flex flex-1 flex-col items-center gap-1.5 px-3 py-3 sm:min-w-[78px]">
    {children}
    <span className="text-[12px] font-medium text-figmaDarkGray">{label}</span>
  </div>
)

/**
 * Compact, full-width version of CandidateStatsCard for the candidate list
 * rows: response / attendance rings + rating, mirroring the detail page's
 * visual language without the height of the full card.
 */
export const CandidateStatStrip = ({
  rating,
  responseRate,
  attendance,
  className = "",
}: {
  rating?: string | number | null
  responseRate?: number | null
  attendance?: number | null
  className?: string
}) => {
  const ratingNum =
    typeof rating === "number" ? rating : parseFloat((rating as string) || "0") || 0

  return (
    <div
      className={`flex w-full items-stretch rounded-2xl border border-[#f0f0f4] bg-[#fafbff] sm:w-auto sm:shrink-0 ${className}`}
    >
      <StripCell label="Răspuns">
        <MiniRing percent={responseRate} />
      </StripCell>

      <div className="w-px self-stretch bg-[#ececf3]" />

      <StripCell label="Prezență">
        <MiniRing percent={attendance} />
      </StripCell>

      <div className="w-px self-stretch bg-[#ececf3]" />

      <StripCell label="Notă">
        <div className="flex h-[46px] items-center gap-1">
          <img src="/images/rate.svg" className="h-fit shrink-0" alt="" />
          <span className="text-[18px] font-bold text-figmaBlack">
            {ratingNum ? ratingNum.toFixed(1) : "—"}
          </span>
        </div>
      </StripCell>
    </div>
  )
}

const Ring = ({ percent }: { percent: number | null | undefined }) => {
  const value = percent != null ? Math.max(0, Math.min(100, percent)) : 0
  return (
    <div
      className="flex h-[72px] w-[72px] items-center justify-center rounded-full"
      style={{ background: `conic-gradient(#486af6 ${value}%, #eef1fe 0)` }}
    >
      <div className="flex h-[58px] w-[58px] items-center justify-center rounded-full bg-white">
        <span className="text-[15px] font-extrabold text-figmaBlack">
          {percent != null ? `${Math.round(percent)}%` : "—"}
        </span>
      </div>
    </div>
  )
}

const StatColumn = ({
  label,
  children,
  subtitle,
}: {
  label: string
  children: React.ReactNode
  subtitle?: string
}) => (
  <div className="flex flex-1 flex-col px-5 py-5">
    <div className="mb-3 flex items-center justify-between">
      <span className="text-[13px] font-semibold text-figmaBlack">{label}</span>
    </div>
    <div className="flex h-[72px] items-center">{children}</div>
    <span className="mt-2.5 text-[12px] font-medium text-figmaDarkGray">{subtitle ?? ""}</span>
  </div>
)

/**
 * Reputation card mirroring the candidate-app stats card: response rate,
 * attendance and reputation (bolts) side by side. Used on candidate /
 * application / invitation detail screens where there is horizontal space.
 */
export const CandidateStatsCard = ({
  rating,
  responseRate,
  responseRateShiftCount,
  attendance,
  attendanceShiftCount,
  numberOfRatings,
  className = "",
}: {
  rating?: string | number | null
  responseRate?: number | null
  responseRateShiftCount?: number | null
  attendance?: number | null
  attendanceShiftCount?: number | null
  numberOfRatings?: number | null
  className?: string
}) => {
  const ratingNum =
    typeof rating === "number" ? rating : parseFloat((rating as string) || "0") || 0

  return (
    <div
      className={`flex w-full overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white ${className}`}
    >
      <StatColumn
        label="Răspuns"
        subtitle={responseRateShiftCount ? `${responseRateShiftCount} invitații` : ""}
      >
        <Ring percent={responseRate} />
      </StatColumn>

      <div className="w-px self-stretch bg-[#e5e7eb]" />

      <StatColumn
        label="Prezență"
        subtitle={attendanceShiftCount ? `${attendanceShiftCount} ture` : ""}
      >
        <Ring percent={attendance} />
      </StatColumn>

      <div className="w-px self-stretch bg-[#e5e7eb]" />

      <StatColumn
        label="Reputație"
        subtitle={numberOfRatings ? `${numberOfRatings} recenzii` : ""}
      >
        <div className="flex flex-col justify-center">
          <div className="flex gap-[1px]">
            {Array.from({ length: 5 }).map((_, i) => (
              <Bolt key={i} filled={ratingNum >= i + 0.5} />
            ))}
          </div>
          <span className="mt-2 text-[24px] font-extrabold leading-none text-figmaBlack">
            {ratingNum.toFixed(1)}
          </span>
        </div>
      </StatColumn>
    </div>
  )
}
